import DynamicForm from "@/common/DynamicForm";
import { IntakeSectionHeader, StyledGridRow } from "@/common/styles";
import { Form } from "antd";
import { useEffect, useRef, useState } from "react";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import { useDispatch, useSelector } from "react-redux";
import { setBookings } from "@/slices/bookingsSlice";
import {
  convertApiFields,
  isNonEmptyVariable,
  hasCheckSecurity,
  getSQLTypeToHTMLType,
  debounce,
} from "@/util/common_function";
import dayjs from "dayjs";

// InmateMedicalExtra's own PK/FK columns, lower-first — mirrors
// AdditionalPersonalInformation.jsx's BioExtra constants.
const INMATE_MEDICAL_EXTRA_ID_KEY = "inmateMedicalExtraID";
const INMATE_MEDICAL_ID_COLUMN = "inmateMedicalID";

// "Additional Inmate Medical Info" tab — PCI's BookingsInterface.asp renders
// this as a supplemental section ("Additional Inmate Medical Information")
// within its single scrollable Inmate Medical submodule; React splits that
// submodule across several separate tabs (Medical Questionnaires, Inmate
// Medications, Medical Authorizations, Appointment Log — see
// BookingsDetails/index.jsx), so this gets its own tab alongside them rather
// than being squeezed onto any one of those.
const AdditionalInmateMedicalInfo = ({ header, onFieldsLoaded }) => {
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings,
  );
  const [form] = Form.useForm();
  const [apiFields, setApiFields] = useState([]);
  const [additionalInmateMedicalInfo, setAdditionalInmateMedicalInfo] =
    useState(null);
  // True once fieldHtmlTypeMapRef is populated — gates the effect below so a
  // date/datetime column's value is never handed to the form as a plain
  // string (see the matching comment in AdditionalPersonalInformation.jsx —
  // same "date.isValid is not a function" crash class).
  const [fieldsReady, setFieldsReady] = useState(false);
  const fullSearch = useSelector((state) => state.fullSearch);

  const bookingsRef = useRef(bookings);
  useEffect(() => {
    bookingsRef.current = bookings;
  }, [bookings]);

  // columnName -> htmlType ("date" / "datetime" / ...) for every
  // InmateMedicalExtra field.
  const fieldHtmlTypeMapRef = useRef({});

  // --- Fetch API Field Definitions ---
  useEffect(() => {
    getFieldsFromExtraAPI();
  }, []);

  // The saved InmateMedicalExtra row itself (with its real PK) is fetched
  // once, centrally, by useInmateMedicalExtraSync in BookingsDetails/
  // index.jsx — not here (see that hook's file header for why a component-
  // local copy of this fetch would race it instead of deduplicating).

  useEffect(() => {
    // Wait for fieldHtmlTypeMapRef to be populated before touching the form
    // — see the fieldsReady comment above for why.
    if (!fieldsReady) return;

    if (isNonEmptyVariable(bookings)) {
      const info = bookings?.inmateMedical?.[0]?.inmateMedicalExtra?.[0];
      // The DatePicker needs an actual dayjs instance to render a
      // date/datetime field's value — redux holds it as a plain string
      // (from the GET fetch, or already-formatted from a prior change), so
      // convert just the known date/datetime columns here rather than
      // guessing at any string that happens to look like a date.
      const withDates = info
        ? Object.keys(info).reduce((acc, key) => {
            const val = info[key];
            const htmlType = fieldHtmlTypeMapRef.current[key];
            const isDateField = htmlType === "date" || htmlType === "datetime";
            acc[key] =
              isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
            return acc;
          }, {})
        : info;
      setAdditionalInmateMedicalInfo(withDates);
    } else {
      setAdditionalInmateMedicalInfo(null);
    }
  }, [bookings, fieldsReady]);

  // Set the form’s initial values from formData (after transforming for display).
  useEffect(() => {
    if (isNonEmptyVariable(additionalInmateMedicalInfo)) {
      form.setFieldsValue(additionalInmateMedicalInfo);
    } else {
      form.resetFields();
    }
  }, [additionalInmateMedicalInfo]);

  const getFieldsFromExtraAPI = () => {
    api
      .get(`${API_ENDPOINTS.getTableColumns}/InmateMedicalExtra`)
      .then((response) => {
        const tableData = response?.data?.filter((d) =>
          d?.columnName?.endsWith("_U"),
        );
        let converted = convertApiFields(tableData, "InmateMedicalExtra");

        const typeMap = {};
        converted.forEach((c) => {
          typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
        });
        fieldHtmlTypeMapRef.current = typeMap;

        setApiFields(converted);
        setFieldsReady(true);
        onFieldsLoaded?.(converted.length > 0);
      })
      .catch((error) => {
        console.error(
          "Request failed:",
          error.response ? error.response.data : error.message,
        );
        onFieldsLoaded?.(false);
      });
  };

  // Merge current visible form values with hidden fields from formData, then
  // transform keys before saving. Reads bookingsRef (not the `bookings`
  // component-scope variable) since this is called from a debounced
  // callback created once (see dataModifyRef below) — the ref always holds
  // the latest redux value, avoiding a stale closure.
  const updateForm = (currentValues) => {
    const inmateMedical = bookingsRef.current?.inmateMedical?.[0] || {};
    const existingExtra = inmateMedical?.inmateMedicalExtra?.[0] || {};

    // A date/datetime field's value is a live dayjs instance here — redux
    // must never hold on to one of those (same reasoning as
    // AdditionalPersonalInformation.jsx's updateForm). Format it to a plain
    // string before it leaves the form, and clear it to null rather than
    // leaving it out, so an emptied field actually clears in redux instead
    // of sticking around.
    const formattedValues = Object.keys(currentValues).reduce((acc, key) => {
      const val = currentValues[key];
      const htmlType = fieldHtmlTypeMapRef.current[key];

      if (val) {
        if (htmlType === "date") {
          acc[key] = dayjs(val).format("MM/DD/YYYY");
        } else if (htmlType === "datetime") {
          acc[key] = dayjs(val).format("MM/DD/YYYY HH:mm");
        } else {
          acc[key] = val;
        }
      } else {
        acc[key] = null;
      }
      return acc;
    }, {});

    dispatch(
      setBookings({
        inmateMedical: [
          {
            ...inmateMedical,
            inmateMedicalExtra: [
              {
                ...existingExtra,
                ...formattedValues,
                [INMATE_MEDICAL_EXTRA_ID_KEY]:
                  existingExtra?.[INMATE_MEDICAL_EXTRA_ID_KEY] ?? 0,
                [INMATE_MEDICAL_ID_COLUMN]:
                  existingExtra?.[INMATE_MEDICAL_ID_COLUMN] ??
                  inmateMedical?.inmateMedicalId ??
                  0,
              },
            ],
          },
        ],
      }),
    );
  };

  // Debounced so rapid keystrokes/onFieldsChange events don't dispatch on
  // every single one — same pattern as AdditionalPersonalInformation.jsx's
  // dataModify. Built once via useRef rather than redeclared each render.
  const dataModifyRef = useRef(null);
  if (!dataModifyRef.current) {
    dataModifyRef.current = debounce(() => {
      updateForm(form.getFieldsValue());
    }, 800);
  }

  return (
    <>
      {header && (
        <IntakeSectionHeader>
          <span>{header}</span>
        </IntakeSectionHeader>
      )}
      <Form
        form={form}
        onFieldsChange={() => {
          dataModifyRef.current();
        }}
        disabled={!bookingsFieldsEnabled}
      >
        <StyledGridRow gutter={[16]}>
          {apiFields.map((sf, index) => {
            const view = hasCheckSecurity(
              "Functionality",
              "field",
              "VIEW",
              "",
              "",
              sf.tableName,
              sf.columnName,
              "Bookings",
            );

            const edit = hasCheckSecurity(
              "Functionality",
              "field",
              "MODIFY",
              "",
              "",
              sf.tableName,
              sf.columnName,
              "Bookings",
            );

            return (
              <DynamicForm
                key={index}
                formAttributes={sf}
                form={form}
                bookingsFieldsEnabled={bookingsFieldsEnabled}
                fullSearch={fullSearch}
                view={view}
                edit={edit}
              />
            );
          })}
        </StyledGridRow>
      </Form>
    </>
  );
};

export default AdditionalInmateMedicalInfo;
